/**
 * Formatting utilities
 */

/**
 * Format date string to locale date
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';

  return d.toLocaleDateString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Taipei',
  });
}

/**
 * Format date string to locale date time
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '-';

  return d.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Taipei',
  });
}

/**
 * Format number as currency
 */
export function formatCurrency(
  amount: number | null | undefined,
  symbol = 'NT$',
  decimals = 0
): string {
  if (amount == null) return '-';

  return `${symbol}${amount.toLocaleString('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * Format number with thousand separators
 */
export function formatNumber(num: number | null | undefined, decimals = 0): string {
  if (num == null) return '-';

  return num.toLocaleString('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format percentage
 */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '-';

  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Truncate string
 */
export function truncate(str: string | null | undefined, maxLength: number): string {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength)}...`;
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Get sale order state label
 */
export function getSaleStateLabel(state: string | null | undefined): string {
  const labels: Record<string, string> = {
    draft: '報價單',
    sent: '已送出',
    sale: '銷售單',
    done: '完成',
    cancel: '取消',
  };
  return state ? labels[state] || state : '-';
}

/**
 * Get construction state label
 */
export function getConstructionStateLabel(state: string | null | undefined): string {
  const labels: Record<string, string> = {
    install: '安裝',
    in_one: '進場一',
    in_two: '進場二',
    in_three: '進場三',
    beauty: '美容',
    repair: '維修',
    finish: '完工',
  };
  return state ? labels[state] || state : '-';
}

/**
 * Get leave state label
 */
export function getLeaveStateLabel(state: string | null | undefined): string {
  const labels: Record<string, string> = {
    draft: '草稿',
    f_approve: '待核准',
    agree: '已核准',
    refused: '已拒絕',
  };
  return state ? labels[state] || state : '-';
}

/**
 * Format ISO datetime to HH:MM
 */
export function formatTime(datetime: string | null | undefined): string {
  if (!datetime) return '--:--';

  // Handle "HH:MM" format directly
  if (/^\d{2}:\d{2}$/.test(datetime)) return datetime;

  // Handle ISO datetime
  const d = new Date(datetime);
  if (isNaN(d.getTime())) return '--:--';

  return d.toLocaleTimeString('zh-TW', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Taipei',
  });
}

/**
 * Get overtime state label
 */
export function getOvertimeStateLabel(state: string | null | undefined): string {
  const labels: Record<string, string> = {
    draft: '草稿',
    f_approve: '待核准',
    agree: '已核准',
    refused: '已拒絕',
  };
  return state ? labels[state] || state : '-';
}

/**
 * Format work hours: 5.5 → "5h 30m"
 */
export function formatWorkHours(hours: number | null | undefined): string {
  if (hours == null) return '-';

  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);

  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
